import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useEditor, useEditingCond, useNode } from '@splashthat/splash-cms-engine';

import SessionCard from 'common/components/SessionCard';
import { getCustomCssClassName } from 'common/components/TouchpointEditor/tools/CustomCssTools/CustomCssTools';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { useSessions } from 'common/components/TouchpointEditor/contexts/SessionsContext';
import { useModal } from 'common/components/TouchpointEditor/contexts/ModalManager';

import { descendantsDefaultProps, descendantsPropTypes } from './defaults';
import { SessionsListBlockContentContainer } from './SessionsList.styles';
import { SessionCardLabel } from './components/SessionCardLabel';
import SessionButtonsWrapper from './SessionButtonsWrapper';
import { NODE_ID_SPEAKER_MODALS, NODE_ID_SESSION_CARDS } from '../../constants';

export default function SessionsList({
    selected,
    hovered,
    descendants: {
        card,
        title,
        date,
        location,
        speakerName,
        speakerTitleCompany,
        description,
        tag,
        statusLabel,
        registerSessionButton,
        registerWaitlistSessionButton,
        addToCalendarButton,
        unregisterSessionButton,
        unregisterWaitlistSessionButton,
    },
}) {
    const node = useNode();
    const editor = useEditor((state) => ({ editing: state.editing }));
    const sessions = useSessions();
    const modal = useModal();
    const {
        theme: { colors, activeFonts },
    } = useTouchpointData();
    const customCssClassName = getCustomCssClassName(node);

    const onClickIfEditing = () => editor.actions.selectNode(NODE_ID_SESSION_CARDS);
    const onClickIfNotEditing = useCallback(
        (speakerInfo) =>
            speakerInfo.bio &&
            modal.open(NODE_ID_SPEAKER_MODALS, {
                speaker: speakerInfo,
            }),
        [],
    );

    const openSpeakerModal = useEditingCond(onClickIfEditing, onClickIfNotEditing);

    return (
        <SessionsListBlockContentContainer
            selected={selected}
            hovered={hovered}
            onMouseLeave={node.actions.onMouseLeave()}
            onMouseMove={node.actions.onMouseMove()}
            onClick={node.actions.onClick()}
            className={customCssClassName}
        >
            {sessions.filteredData.map((session) => (
                <SessionCard
                    key={session.id}
                    session={session}
                    colors={colors}
                    activeFonts={activeFonts}
                    cardStyles={card.styles}
                    titleStyles={title.styles}
                    dateStyles={date.styles}
                    speakerNameStyles={speakerName.styles}
                    speakerTitleCompanyStyles={speakerTitleCompany.styles}
                    descriptionStyles={description.styles}
                    tagStyles={tag.styles}
                    labelComponent={
                        <SessionCardLabel session={session} styles={statusLabel.styles} meta={statusLabel.meta} />
                    }
                    onClickSpeakerAvatar={openSpeakerModal}
                    footerComponent={
                        <SessionButtonsWrapper
                            session={session}
                            registerSessionButton={registerSessionButton}
                            registerWaitlistSessionButton={registerWaitlistSessionButton}
                            addToCalendarButton={addToCalendarButton}
                            unregisterSessionButton={unregisterSessionButton}
                            unregisterWaitlistSessionButton={unregisterWaitlistSessionButton}
                            disableContentEditing={!editor.editing || (!selected && !hovered)}
                        />
                    }
                />
            ))}
        </SessionsListBlockContentContainer>
    );
}

SessionsList.splash = {
    related: {
        settings: React.lazy(() => import('./SessionsList.tools')),
    },
    defaultProps: {
        styles: {},
        descendants: descendantsDefaultProps, // THIS IS AN EXCEPTION FOR THE AGENDA TOUCHPOINT
    },
};

SessionsList.propTypes = {
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    descendants: PropTypes.shape(descendantsPropTypes).isRequired,
};

SessionsList.defaultProps = {
    className: '',
};
